import { useEffect, useState } from 'react'
import { Table, Space, Button, Col, Row, Pagination, Checkbox, Tag, Select } from 'antd'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { useStore } from '../../hooks'
import { DATE_TIME_FORMAT_CLIENT, getPage, PAGE_SIZE } from '../../helpers/constants'
import { GLOBAL_CLIENT } from '../../helpers/GlobalClient'
import useWindowDimensions from '../../common/useWindowDimensions'
import useModal from 'src/helpers/modal/useModal'
import { showMessageError, showMessageSuccess } from 'src/helpers/functions'
import Create from './Create'
import ShowKey from './ShowKey'
import KeyCell from './KeyCell'

const ApiKeyManagementScreen = observer(() => {
   const { t } = useTranslation()
   const ApiKeyStore = useStore('ApiKeyStore')
   const UserStore = useStore('UserStore')
   const { height } = useWindowDimensions()
   const { confirm } = useModal()

   const [visibleCreate, setVisibleCreate] = useState(false)
   const [createdKey, setCreatedKey] = useState('')
   const [includeRevoked, setIncludeRevoked] = useState(false)
   const [accountId, setAccountId] = useState<number | undefined>(undefined)
   const [currentPage, setCurrentPage] = useState(0)

   const { rows, offset, count } = ApiKeyStore.listApiKey

   useEffect(() => {
      handle_getListData(0)
      setCurrentPage(0)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [includeRevoked, accountId])

   const handle_getListData = async (page: number) => {
      const params: any = { offset: getPage(page), limit: PAGE_SIZE, includeRevoked }
      if (accountId) params.accountId = accountId
      await ApiKeyStore.getApiKeys(params)
   }

   const onChange = (page: number) => {
      setCurrentPage(page - 1)
      handle_getListData(page - 1)
   }

   const showCreate = () => setVisibleCreate(true)

   const handleCreated = (apiKey: string) => {
      setVisibleCreate(false)
      setCreatedKey(apiKey)
      handle_getListData(currentPage)
   }

   const closeShowKey = () => {
      setCreatedKey('')
      ApiKeyStore.clearCreatedKey()
   }

   const handleRevoke = (record: any) => {
      confirm({
         title: 'Thu hồi API key',
         content: `Key <b>${record.prefix}</b> sẽ ngừng hoạt động ngay lập tức và không thể khôi phục. Bạn chắc chắn?`,
         okText: 'Thu hồi',
         cancelText: t(GLOBAL_CLIENT.huy),
         onCancel: () => {},
         onOk: async () => {
            const ok = await ApiKeyStore.revokeApiKey(record.id)
            if (ok) {
               showMessageSuccess('Đã thu hồi API key')
               handle_getListData(currentPage)
            } else {
               showMessageError('Thu hồi thất bại')
            }
         }
      })
   }

   const handleDelete = (record: any) => {
      confirm({
         title: 'Xoá API key',
         content: `Key <b>${record.prefix}</b> sẽ bị xoá khỏi danh sách. Bạn chắc chắn?`,
         okText: t(GLOBAL_CLIENT.delete),
         cancelText: t(GLOBAL_CLIENT.huy),
         onCancel: () => {},
         onOk: async () => {
            const ok = await ApiKeyStore.deleteApiKey(record.id)
            if (ok) {
               showMessageSuccess('Đã xoá API key')
               handle_getListData(currentPage)
            } else {
               showMessageError('Xoá thất bại')
            }
         }
      })
   }

   const renderStatus = (record: any) => {
      if (record.revokedAt) return <Tag color="red">{'Đã thu hồi'}</Tag>
      if (record.expiresAt && moment().isAfter(moment(record.expiresAt))) return <Tag color="orange">{'Hết hạn'}</Tag>
      return <Tag color="green">{'Đang hoạt động'}</Tag>
   }

   const columns = [
      {
         title: t(GLOBAL_CLIENT.id),
         dataIndex: 'id',
         key: 'id',
         align: 'center' as 'center',
         width: 60
      },
      {
         title: 'Tài khoản',
         dataIndex: 'account',
         key: 'account',
         width: 180,
         render: (value: any) => {
            if (!value) return <span>-</span>
            return (
               <span className="txtMax2Line">
                  {value.name || value.email}
                  <br />
                  <span style={{ color: '#888' }}>{`${value.email} (${value.role})`}</span>
               </span>
            )
         }
      },
      {
         title: 'Tên gợi nhớ',
         dataIndex: 'name',
         key: 'name',
         width: 140,
         render: (value: string) => <span className="txtMax2Line">{value || '-'}</span>
      },
      {
         title: 'Mã key',
         dataIndex: 'prefix',
         key: 'prefix',
         width: 260,
         render: (value: string, record: any) => <KeyCell apiKey={record.apiKey} prefix={value} />
      },
      {
         title: 'Trạng thái',
         key: 'status',
         width: 110,
         align: 'center' as 'center',
         render: (text: any, record: any) => renderStatus(record)
      },
      {
         title: 'Dùng lần cuối',
         dataIndex: 'lastUsedAt',
         key: 'lastUsedAt',
         width: 110,
         render: (value: string) => (
            <span className="txtMax2Line">{value ? moment(value).format(DATE_TIME_FORMAT_CLIENT) : 'Chưa dùng'}</span>
         )
      },
      {
         title: 'Hết hạn',
         dataIndex: 'expiresAt',
         key: 'expiresAt',
         width: 110,
         render: (value: string) => (
            <span className="txtMax2Line">{value ? moment(value).format(DATE_TIME_FORMAT_CLIENT) : 'Không'}</span>
         )
      },
      {
         title: t(GLOBAL_CLIENT.createAt),
         dataIndex: 'createdAt',
         key: 'createdAt',
         width: 110,
         render: (value: string) => <span className="txtMax2Line">{moment(value).format(DATE_TIME_FORMAT_CLIENT)}</span>
      },
      {
         title: 'Action',
         key: 'action',
         width: 130,
         align: 'center' as 'center',
         fixed: 'right' as 'right',
         render: (text: string, record: any) => (
            <Space>
               {!record.revokedAt && (
                  <span onClick={() => handleRevoke(record)} className="gx-link">
                     {'Thu hồi'}
                  </span>
               )}
               <span onClick={() => handleDelete(record)} className="gx-link">
                  {t(GLOBAL_CLIENT.delete)}
               </span>
            </Space>
         )
      }
   ]

   const renderPagination = () => {
      if (isEmpty(rows)) return null
      return (
         <div className="containerPagination">
            <Pagination
               onChange={onChange}
               pageSize={PAGE_SIZE}
               current={offset / PAGE_SIZE + 1}
               showSizeChanger={false}
               total={count}
            />
         </div>
      )
   }

   const accountOptions = [...(UserStore.listCreator?.rows || []), ...(UserStore.listDesigner?.rows || [])]

   return (
      <>
         <Row>
            <Col span={24}>
               <Row className="row-container-head">
                  <Col xs={24} md={12}>
                     <Space wrap>
                        <Select
                           allowClear
                           showSearch
                           style={{ minWidth: 240 }}
                           placeholder={'Lọc theo tài khoản'}
                           value={accountId}
                           onChange={(value: any) => setAccountId(value)}
                           optionFilterProp="children"
                           filterOption={(input, option) =>
                              String(option?.children ?? '')
                                 .toLowerCase()
                                 .indexOf(input.toLowerCase()) >= 0
                           }
                        >
                           {accountOptions.map((item: any) => (
                              <Select.Option key={item.id} value={item.id}>
                                 {`${item.name || item.email} - ${item.email}`}
                              </Select.Option>
                           ))}
                        </Select>
                        <Checkbox
                           checked={includeRevoked}
                           onChange={(e) => setIncludeRevoked(e.target.checked)}
                           className="gx-pointer"
                        >
                           {'Hiện cả key đã thu hồi'}
                        </Checkbox>
                     </Space>
                  </Col>
                  <Col xs={24} md={12}>
                     <div className="text-right">
                        <Space>
                           <Button type="primary" className="btn-add" onClick={showCreate}>
                              <i className="icon icon-add icon-in-button" />
                              {t(GLOBAL_CLIENT.create)}
                           </Button>
                        </Space>
                     </div>
                  </Col>
               </Row>

               <Space className="total-items">
                  {t(GLOBAL_CLIENT.totalItems)}: {count}
               </Space>

               <Table
                  scroll={{ x: 1100, y: height - 280 }}
                  columns={columns}
                  dataSource={rows}
                  rowKey="id"
                  pagination={false}
               />

               {renderPagination()}
            </Col>
         </Row>

         {visibleCreate && (
            <Create visible={visibleCreate} onOk={handleCreated} onCancel={() => setVisibleCreate(false)} />
         )}
         {!!createdKey && <ShowKey visible={!!createdKey} apiKey={createdKey} onClose={closeShowKey} />}
      </>
   )
})

export default ApiKeyManagementScreen
